import { useQuery } from '@apollo/client';

import { MEMBERSHIP_ACCESS } from '../../components/MembershipNotice';

import type {
  MembershipAccessQuery,
  MembershipAccessQueryVariables,
} from '../../components/MembershipNotice/__generated__/MembershipNotice.gql';

/**
 * The household's `Family.hasMembershipAccess`, or `undefined` until it loads.
 *
 * Reads the shell notice's `MembershipAccess` document, so the assistant
 * socket's `4004` refetch by name updates every caller. Branch on an explicit
 * `false`: the field is `true` wherever the wall is switched off, so an absent
 * answer is a cold cache far more often than a lapse.
 *
 * @returns `true` or `false` once the read lands; `undefined` while it is in
 *   flight or after it failed.
 */
export function useMembershipAccess(): boolean | undefined {
  const { data } = useQuery<MembershipAccessQuery, MembershipAccessQueryVariables>(MEMBERSHIP_ACCESS);
  return data?.getCurrentFamily?.hasMembershipAccess;
}

/**
 * `Family.viewerCanManageBilling` from the same document: `false` for a loved
 * one, whose role the server denies every billing read.
 *
 * @returns `true` or `false` once the read lands; `undefined` until then.
 */
export function useViewerCanManageBilling(): boolean | undefined {
  const { data } = useQuery<MembershipAccessQuery, MembershipAccessQueryVariables>(MEMBERSHIP_ACCESS);
  return data?.getCurrentFamily?.viewerCanManageBilling;
}
